import { Component, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TenantAuthService } from '../../services/tenant-auth.service';
import { ADMIN_MENU_ICONS_PROVIDER, AdminConfirmDialogHostComponent, AdminToastHostComponent } from '../../shared/admin';
import {
  AdminSidebarComponent,
  isAdminMenuGroup,
  isAdminMenuSection,
  type AdminMenuItem,
} from '../../platform/layout/admin-sidebar.component';
import { AdminTopbarComponent, type AdminTopbarUser } from '../../platform/layout/admin-topbar.component';
import { buildPageTitle } from '../../shared/constants/site-meta';

@Component({
  selector: 'app-tenant-layout',
  imports: [RouterOutlet, AdminSidebarComponent, AdminTopbarComponent, AdminToastHostComponent, AdminConfirmDialogHostComponent],
  standalone: true,
  providers: [ADMIN_MENU_ICONS_PROVIDER],
  templateUrl: './tenant-layout.component.html',
})
export class TenantLayoutComponent {
  private readonly tenantAuth = inject(TenantAuthService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);

  readonly sidebarCollapsed = signal(false);
  readonly mobileNavOpen = signal(false);

  private readonly fullMenu: AdminMenuItem[] = [
    { label: 'Dashboard', route: '/tenant/dashboard' },
    { label: 'Leads', route: '/tenant/leads' },
    { label: 'Enrolled Applicants', route: '/tenant/applicants' },
    { label: 'Staff', route: '/tenant/staff', roles: ['FRANCHISE_ADMIN'] },
    { label: 'Settings', route: '/tenant/settings' },
  ];

  readonly menu = computed(() => {
    const role = this.tenantAuth.currentUser()?.role;
    return this.fullMenu.filter((m) => {
      if (isAdminMenuSection(m) || isAdminMenuGroup(m)) return true;
      return !m.roles || (role && m.roles.includes(role));
    });
  });

  readonly topbarUser = computed<AdminTopbarUser | null>(() => {
    const u = this.tenantAuth.currentUser();
    if (!u) return null;
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
    return { name, roleKey: u.role };
  });

  private readonly activeUrl = signal(this.router.url);

  readonly pageTitle = computed(() => {
    const url = this.activeUrl();
    const match = this.menu().find(
      (m): m is Extract<AdminMenuItem, { route: string }> =>
        !isAdminMenuSection(m) &&
        !isAdminMenuGroup(m) &&
        (url === m.route || url.startsWith(m.route + '/')),
    );
    return match?.label ?? 'Franchise Portal';
  });

  constructor() {
    effect(() => {
      this.title.setTitle(buildPageTitle(this.pageTitle()));
    });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((e) => this.activeUrl.set(e.urlAfterRedirects));
  }

  toggleSidebar(): void {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      this.mobileNavOpen.update((v) => !v);
      return;
    }
    this.sidebarCollapsed.update((v) => !v);
  }

  closeMobileNav(): void {
    this.mobileNavOpen.set(false);
  }

  async logout(): Promise<void> {
    await this.tenantAuth.logout();
  }
}
